<template>
  <ankh-list>
    <template #btn-group>
      <el-button type="primary" @click.native="handleAdd">{{$t('新增')}}</el-button>
      <el-button @click.native="handleBatchDelete">{{$t('批量删除')}}</el-button>
    </template>

    <!-- 搜索表单区：遵循系统统一表单间距、大小与布局 -->
    <template #search-form>
      <!-- 算法名称搜索 -->
      <el-form-item :label="$t('算法名称')">
        <el-input v-model="searchForm.name" clearable :placeholder="$t('请输入算法名称')" />
      </el-form-item>
      <!-- 算法类型搜索 -->
      <el-form-item :label="$t('算法类型')">
        <el-select v-model="searchForm.type" clearable :placeholder="$t('请选择算法类型')">
          <el-option :label="$t('推荐值')" value="RECOMMEND" />
          <el-option :label="$t('BMI')" value="BMI" />
          <el-option :label="$t('体重')" value="WEIGHT" />
        </el-select>
      </el-form-item>
      <!-- 操作按钮与统一交互 -->
      <el-form-item>
        <el-button type="primary" size="small" @click="$refs.list.handleSearch()">{{ $t('搜索') }}</el-button>
        <el-button size="small" @click="$refs.list.reset()">{{ $t('重置') }}</el-button>
      </el-form-item>
    </template>

    <!-- 表格区：系统封装TableList，接入高度传递、选择与查询事件 -->
    <template #table="{height}">
      <table-list
          ref="list"
          :height="height"
          :request-params="{refRecommend: $route.query.code}"
          @query="handleSearch"
          :search-params="searchForm"
          :data="tableData"
          @selection-change="selection = $event">

        <el-table-column type="selection" width="50" />
        <el-table-column type="index" :label="$t('序号')" width="60" fixed />

        <el-table-column prop="name" :label="$t('算法名称')" min-width="200" show-overflow-tooltip />
        <el-table-column prop="type" :label="$t('算法类型')" min-width="120">
          <template v-slot="{row}">
            <el-tag :type="getTypeTagType(row.type)">
              {{ getTypeLabel(row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="algo" :label="$t('配置')" min-width="300" show-overflow-tooltip />

        <el-table-column :label="$t('操作')" width="150" fixed="right" align="center">
          <template v-slot="{row}">
            <div class="fb-link-flex">
              <el-link :underline="false" type="primary" @click="handleEdit(row)">{{ $t('编辑') }}</el-link>
              <el-link :underline="false" type="danger" @click="handleDelete(row)">{{ $t('删除') }}</el-link>
            </div>
          </template>
        </el-table-column>

      </table-list>
    </template>

    <!-- 弹窗区：预留新增/编辑表单弹窗位点，保持统一行为与间距 -->
    <template #other>
      <el-dialog :visible.sync="showDialog" :title="dialogTitle" :close-on-click-modal="false" width="600px" @close="resetDialog" class="algorithm-dialog">
        <el-form :model="form" :rules="rules" ref="form" label-width="100px" class="compact-form">
          <el-form-item :label="$t('算法名称')" prop="name">
            <el-input v-model="form.name" :placeholder="$t('请输入算法名称')" />
          </el-form-item>
          <el-form-item :label="$t('算法类型')" prop="type">
            <el-select v-model="form.type" :placeholder="$t('请选择算法类型')" style="width: 100%">
              <el-option :label="$t('推荐值')" value="RECOMMEND" />
              <el-option :label="$t('BMI')" value="BMI" />
              <el-option :label="$t('体重')" value="WEIGHT" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('配置')" prop="algo">
            <el-input v-model="form.algo" :placeholder="$t('请输入配置')" />
            <div class="tip-box">
              <span>{{$t('示例：66 + (6.23 x 体重（kg）) + (12.7 x 身高（cm）) - (6.8 x 年龄)')}}</span>
              <span class="tip-warn">{{$t('注意：字段名称必须与系统字段一致')}}</span>
            </div>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showDialog = false">{{ $t('取消') }}</el-button>
          <el-button type="primary" @click="handleSubmit">{{ $t('确定') }}</el-button>
        </span>
      </el-dialog>
    </template>
  </ankh-list>
</template>

<script>
import AnkhList from '@/components/list/AnkhList.vue'
import TableList from '@/components/table-list/TableList'
import { request, requestByTip } from '@/config/request'

export default {
  name: 'AlgoList',
  components: {AnkhList, TableList },
  data() {
    return {
      // 搜索表单
      searchForm: {
        name: '',
        type: '',
      },
      // 表格数据
      tableData: [],
      // 选择项
      selection: [],
      // 弹窗
      showDialog: false,
      isEdit: false,
      form: {
        code: '', // 主键
        refRecommend: '', // 关联的推荐值配置
        name: '',
        type: '',
        algo: '', // 算法字段
      },
      // 表单校验规则
      rules: {
        name: [{ required: true, message: this.$t('请输入算法名称'), trigger: 'blur' }],
        type: [{ required: true, message: this.$t('请选择算法类型'), trigger: 'change' }],
        algo: [{ required: true, message: this.$t('请输入算法'), trigger: 'blur' }],
      },
    }
  },
  methods: {
    // 查询列表：保持与系统TableList事件对接
    async handleSearch(params, setTotal) {
      try {
        // 合并搜索与分页参数；request封装会过滤空值
        const query = Object.assign({}, params)
        const resp = await request(this.$api.algo.list, query)
        this.tableData = resp.algos || []
        setTotal(resp.pager?.itemCount || resp.total);
      } catch (e) {
        // 静默失败，交由request内部提示
      }
    },
    // 新增
    handleAdd() {
      this.isEdit = false
      this.showDialog = true
      this.form = {
        code: '', // 新增时code为空
        refRecommend: this.$route.query.code || '', // 从路由参数获取
        name: '',
        type: '',
        algo: ''
      }
    },
    // 编辑
    handleEdit(row) {
      this.isEdit = true
      this.showDialog = true
      this.form = Object.assign({}, row)
      // 确保refRecommend字段存在
      if (!this.form.refRecommend) {
        this.form.refRecommend = this.$route.query.code || ''
      }
    },
    // 删除
    async handleDelete(row) {
      try {
        await requestByTip(this.$api.algo.delete, { code: row.code })
        this.$message.success(this.$t('操作成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 批量删除
    async handleBatchDelete() {
      if (!this.selection || !this.selection.length) {
        this.$message.warning(this.$t('请选择需要删除的记录'))
        return
      }
      try {
        const codes = this.selection.map(it => it.code).filter(Boolean)
        if (!codes.length) {
          this.$message.error(this.$t('所选记录缺少主键code'))
          return
        }
        await requestByTip(
          this.$api.algo.batchDelete,
          codes,
          { isJson: true },
          { message: this.$t('您现在正在进行批量删除操作, 是否继续?') }
        )
        this.$message.success(this.$t('操作成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 提交（新增/编辑）
    async handleSubmit() {
      try {
        await this.$refs.form.validate()

        // 构建提交数据，严格按照系统现有请求方式
        const submitData = {
          refRecommend: this.form.refRecommend || this.$route.query.code || '', // 关联的推荐值配置
          name: this.form.name, // 名称
          type: this.form.type, // 类型
          algo: this.form.algo, // 算法
        }

        // 编辑时需要携带code主键
        if (this.isEdit && this.form.code) {
          submitData.code = this.form.code
        }

        // 使用algo接口而不是algorithm接口
        const api = this.isEdit ? this.$api.algo.update : this.$api.algo.add
        await request(api, submitData)
        this.$message.success(this.$t('保存成功'))
        this.showDialog = false
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 重置弹窗
    resetDialog() {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate && this.$refs.form.clearValidate()
      })
    },
    // 获取算法类型标签类型
    getTypeTagType(type) {
      const typeMap = {
        'recommend': 'primary',
        'bmi': 'success'
      }
      return typeMap[type] || 'default'
    },
    // 获取算法类型标签文本
    getTypeLabel(type) {
      const typeMap = {
        'RECOMMEND': this.$t('推荐值'),
        'BMI': 'BMI',
        'WEIGHT': this.$t('体重')
      }
      return typeMap[type] || type
    },
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? this.$t('编辑算法配置') : this.$t('新增算法配置')
    }
  }
}
</script>

<style scoped>
.fb-link-flex {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.algorithm-dialog .el-dialog {
  border-radius: 8px;
}

.algorithm-dialog .el-dialog__header {
  text-align: left;
  padding: 20px 20px 10px;
}

.algorithm-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.algorithm-dialog .el-dialog__title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 24px;
  background: #0077F7;
  position: absolute;
  top: 20px;
  left: 0;
}

.algorithm-dialog .el-dialog__body {
  padding: 10px 20px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.algorithm-dialog .compact-form .el-form-item {
  margin-bottom: 18px;
}

.algorithm-dialog .compact-form .el-form-item__label {
  font-weight: 500;
  color: #303133;
}

.algorithm-dialog .dialog-footer {
  text-align: right;
  padding: 10px 20px 20px;
}

.algorithm-dialog .dialog-footer .el-button {
  margin-left: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .algorithm-dialog .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}

.tip-box {
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  line-height: 22px;
  color: #909399;
}

.tip-warn {
  color: #f56c6c;
}

</style>
